import moment from "moment";
import { useParams, useNavigate } from "react-router";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import MainLayout from "@/components/layouts";
import { useAppointmentDetail } from "./appointment-detail.hooks";
import { fetchAppointment } from "./appointments.api";
import AppointmentStatusBadge from "./components/AppointmentStatusBadge";
import { Button } from "@/components/ui/button.tsx";
import ActionsDropdown from "@/app/pmc/appointments/components/ActionsDropdown.tsx";
import EditCommentModal from "@/app/pmc/appointments/components/EditAppointmentCommentModal.tsx";
import { TaskChecklist } from "@/components/shared/TaskChecklist";
import { PhotoUploadSection } from "./components/PhotoUploadSection";
import { Lock, MessageCircle, Copy, Check } from "lucide-react";
import AppointmentCommentsToggle from "@/components/shared/AppointmentCommentsToggle";
import { AppointmentChatService } from "@/services/appointment-chat.service";
import { toast } from "sonner";
import { appointment_status, AppointmentClockData, getExtraPhotos} from "@/models/appointment.model";
import { Skeleton } from "@/components/ui/skeleton";
import { ActivityLog } from "@/components/shared/activity-log";
import { ClockSummary } from "@/components/shared/clock-summary";
import { PreviousWorkSection } from "@/components/shared/PreviousWorkSection";
import { FINISHED_OR_COMPLETED } from "@/repositories/appointment-records";

export default function AppointmentDetailPage() {
  const { appointmentId: id = "" } = useParams();
  const navigate = useNavigate();
  const [loadingChat, setLoadingChat] = useState(false);
  const [copied, setCopied] = useState(false);
  const {
    loading,
    loadingSecondary,
    appointment,
    setAppointment,
    logs,
    refreshLogs,
    serviceTasks,
    serviceWorkflows,
    appointmentTasks,
    appointmentWorkflows,
    error,
    editOpen,
    setEditOpen,
  } = useAppointmentDetail(id);

  const reReadAfterRefusal = async () => {
    try {
      setAppointment(await fetchAppointment(id));
    } catch (error) {
      console.error("Failed to reload the appointment:", error);
      toast.error("Failed to reload the appointment");
    }
    await refreshLogs();
  };

  const handleOpenChat = async () => {
    if (!appointment?.id) return;

    setLoadingChat(true);
    try {
      const chatRoom = await AppointmentChatService.getAppointmentGroupChat(appointment.id);
      if (chatRoom) {
        navigate(`/messages/${chatRoom.id}`);
      } else {
        toast.error("Chat room not found for this appointment");
      }
    } catch (error) {
      console.error("Failed to open chat:", error);
      toast.error("Failed to open chat");
    } finally {
      setLoadingChat(false);
    }
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(id);
    setCopied(true);
    toast.success("Appointment ID copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const breadcrumb = [
    { name: "Appointments", href: "/appointments", current: false },
    { name: `Booking #${id.slice(0, 8)}`, href: "#", current: true },
  ];

  if (loading) {
    return (
      <MainLayout breadcrumb={breadcrumb}>
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-40" />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-center gap-4 col-span-full">
                  <Skeleton className="w-16 h-16 rounded-md flex-shrink-0" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-5 w-40" />
                    <Skeleton className="h-4 w-52" />
                    <Skeleton className="h-4 w-44" />
                  </div>
                </div>
                <div className="col-span-full grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Array.from({ length: 9 }).map((_, i) => (
                    <div key={i} className="space-y-2">
                      <Skeleton className="h-3 w-20" />
                      <Skeleton className="h-5 w-28" />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Skeleton className="h-10 w-full rounded-lg" />
                <Skeleton className="h-16 w-full rounded-lg" />
                <Skeleton className="h-16 w-full rounded-lg" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-36" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Skeleton className="h-10 w-full rounded-lg" />
                <Skeleton className="h-16 w-full rounded-lg" />
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  if (error || !appointment)
    return (
      <MainLayout breadcrumb={breadcrumb}>
        <p className="text-destructive">{error ?? "Appointment not found"}</p>
      </MainLayout>
    );

  const appt = appointment;
  const previousWork = (appt.meta_data as any)?.previous_work as import("@/models/appointment.model").PreviousWorkSnapshot[] | undefined;
  const clockInData = appt.meta_data?.clock_in as AppointmentClockData | undefined;
  const clockOutData = appt.meta_data?.clock_out as AppointmentClockData | undefined;
  const doneAt = (appt.meta_data as any)?.done_at as string | undefined;
  const gobackAt = (appt.meta_data as any)?.goback_at as string | undefined;

  return (
    <MainLayout
      breadcrumb={breadcrumb}
      pageHeader={{
        title: `Booking #${id.slice(0, 8)}`,
        subtitle: "Appointment details and documentation",
        additionalButtons: (
          <div className="hidden sm:flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleCopyId} className="gap-1.5">
              {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied" : "Copy ID"}
            </Button>
            <Button variant="outline" size="sm" onClick={handleOpenChat} disabled={loadingChat}>
              <MessageCircle className="h-4 w-4 mr-2" />
              {loadingChat ? "Loading..." : "Chat"}
            </Button>
            <ActionsDropdown row={appt as any} onRefetch={reReadAfterRefusal} />
          </div>
        ),
      }}
    >
      <div className="container mx-auto space-y-6">
        {/* Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="uppercase">Overview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Row 1: Property */}
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16 rounded-md flex-shrink-0">
                <AvatarImage
                  src={
                    appt.property?.image ??
                    "https://app.shynenetwork.com/assets/signupAsset/images/apartment_default.jpg"
                  }
                  alt={appt.property.name}
                />
                <AvatarFallback className="rounded-md">{appt.property.name[0]}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold">{appt.property.name}</h2>
                <p className="text-sm text-muted-foreground">{appt.property?.address ?? "No address provided"}</p>
                <p className="text-sm text-muted-foreground">
                  Unit: {appt.property.unit_number} | Beds: {appt.property.beds} | Baths: {appt.property.baths}
                </p>
              </div>
            </div>

            {/* Row 2: Appointment ID  -  Status */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Info label="Appointment ID">
                <span className="flex items-center gap-1.5">
                  {appt.id.slice(0, 8)}
                  <button
                    onClick={handleCopyId}
                    className="text-muted-foreground hover:text-primary transition-colors sm:hidden"
                  >
                    {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                </span>
              </Info>
              <Info label="Status">
                <AppointmentStatusBadge status={appt.status} />
              </Info>
            </div>

            {/* Row 3: Service  -  Price  -  Vendor */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Info label="Service">
                {appt.service.name}
                <br />
                <span className="text-muted-foreground text-sm">{appt.service.parent_name}</span>
              </Info>
              {appt.final_price != null && <Info label="Price">${appt.final_price.toFixed(2)}</Info>}
              <Info label="Vendor">{appt.vendor.name}</Info>
            </div>

            {/* Row 4: Scheduled Date  -  Created Date  -  Done At / Go Back At */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Info label="Scheduled Date">{moment(appt.date).format("MM/DD/YYYY, h:mm a")}</Info>
              <Info label="Created Date">{moment(appt.created_at).format("MM/DD/YYYY")}</Info>
              {FINISHED_OR_COMPLETED.includes(appt.status) && doneAt && (
                <Info label="Done At">{moment(doneAt).format("MM/DD/YYYY, h:mm a")}</Info>
              )}
              {appt.status === appointment_status.goback && gobackAt && (
                <Info label="Go Back At">{moment(gobackAt).format("MM/DD/YYYY, h:mm a")}</Info>
              )}
              {appt.work_order_notes && (
                <div className="col-span-2 flex items-start gap-2">
                  <div>
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-1">Work Order Notes</h3>
                    <p className="text-base font-medium">{appt.work_order_notes}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setEditOpen(true)}>
                    Edit
                  </Button>
                </div>
              )}
              <AppointmentCommentsToggle appointmentId={appt.id} userRole="pmc" onNoteAdded={refreshLogs} />
            </div>

            {/* Cancellation Details */}
            {appt.status === appointment_status.cancelled && appt.meta_data && (
              <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-md p-4 space-y-2">
                <h3 className="text-sm font-semibold text-red-800 dark:text-red-200 uppercase">Cancellation Details</h3>
                {(appt.meta_data as any).cancellation_notes && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Reason</p>
                    <p className="text-sm text-red-900 dark:text-red-100">
                      {(appt.meta_data as any).cancellation_notes}
                    </p>
                  </div>
                )}
                <div className="flex items-center gap-4">
                  {appt.cancelled_by_user?.name && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Cancelled By</p>
                      <p className="text-sm text-red-900 dark:text-red-100">{appt.cancelled_by_user.name}</p>
                    </div>
                  )}
                  {(appt.meta_data as any).cancelled_at && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Cancelled On</p>
                      <p className="text-sm text-red-900 dark:text-red-100">
                        {moment((appt.meta_data as any).cancelled_at).format("MM/DD/YYYY, h:mm a")}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Clock Summary */}
        {clockInData?.time && <ClockSummary clockIn={clockInData} clockOut={clockOutData} />}

        {/* Previous Work (shown when appointment has been through goback) */}
        {previousWork && <PreviousWorkSection previousWork={previousWork} />}

        {/* Photo Documentation */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="uppercase">Photo Documentation</CardTitle>
              {appt.status === "done" && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Lock className="w-4 h-4" />
                  <span className="text-sm">Read Only</span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loadingSecondary ? (
              <div className="space-y-3">
                <Skeleton className="h-10 w-full rounded-lg" />
                <Skeleton className="h-16 w-full rounded-lg" />
                <Skeleton className="h-16 w-full rounded-lg" />
              </div>
            ) : (
              <PhotoUploadSection
                serviceWorkflows={serviceWorkflows}
                appointmentWorkflows={appointmentWorkflows}
                onPhotoUpload={() => {}}
                readOnly={true}
                clockInData={clockInData}
                clockOutData={clockOutData}
                extraPhotos={getExtraPhotos(appt.meta_data, false).concat(getExtraPhotos(appt.meta_data, true))}
              />
            )}
          </CardContent>
        </Card>

        {/* Tasks */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="uppercase">Service Tasks</CardTitle>
              {appt.status === "done" && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Lock className="w-4 h-4" />
                  <span className="text-sm">Read Only</span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loadingSecondary ? (
              <div className="space-y-3">
                <Skeleton className="h-10 w-full rounded-lg" />
                <Skeleton className="h-16 w-full rounded-lg" />
              </div>
            ) : (
              <TaskChecklist
                serviceTasks={serviceTasks}
                appointmentTasks={appointmentTasks}
                onTaskToggle={() => {}}
                readOnly={true}
              />
            )}
          </CardContent>
        </Card>

        {/* Activity Log */}
        <Card>
          <CardHeader>
            <CardTitle className="uppercase">Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <ActivityLog logs={logs} />
          </CardContent>
        </Card>
      </div>

      <EditCommentModal
        id={appt.id}
        current={appt.work_order_notes ?? ""}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={(notes) => {
          setAppointment({ ...appt, work_order_notes: notes });
          void refreshLogs();
        }}
      />
    </MainLayout>
  );
}

function Info({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-1">{label}</h3>
      <p className="text-base font-medium leading-snug">{children}</p>
    </div>
  );
}
